import {Component, DestroyRef, TemplateRef, computed, effect, inject, input, OnDestroy, OnInit, output, signal, untracked} from '@angular/core';
import dayjs from 'dayjs/esm';
type Dayjs = dayjs.Dayjs;
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable, Subject, merge} from 'rxjs';
import {FormArray, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Option, CustomValidators, XmedicalUiModule} from '@xmedical/ui';
import {
  AddressFormShape,
  BillingContactFormShape,
  ContactNumberFormShape,
  DoctorFormShape,
  EmailAddressFormShape,
  MedicalAidFormShape,
  PatientForm,
  PatientFormService,
  SecondaryContactFormShape,
  formValueToCreatePayload,
  formValueToUpdatePayload,
  makeContactNumberFormGroup,
  makeDoctorFormGroup,
  makeSecondaryContactFormGroup,
} from './patient-form.service';
import {debounceTime, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {CommonModule, DatePipe} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDividerModule} from '@angular/material/divider';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {Doctor, MedicalScheme, Patient, PatientCreatePayload, PatientUpdatePayload, QueryPatient} from '@xmedical/core';
import {PatientDraft, PatientDraftStore, XCS_PATIENT_DRAFT_STORE} from './patient-draft-store';
import {PatientDuplicatesBannerComponent} from './patient-duplicates-banner/patient-duplicates-banner.component';
import {PatientContactMethodsFieldsetComponent} from './patient-contact-methods-fieldset/patient-contact-methods-fieldset.component';
import {PatientDoctorsFieldsetComponent} from './patient-doctors-fieldset/patient-doctors-fieldset.component';
import {PatientBillingContactFieldsetComponent} from './patient-billing-contact-fieldset/patient-billing-contact-fieldset.component';
import {PatientMedicalAidFieldsetComponent} from './patient-medical-aid-fieldset/patient-medical-aid-fieldset.component';

// Discriminated union the form emits via (save). The container handles
// persistence (calls PatientStore.create / .update) and post-success side
// effects (open tab, navigate, clear draft) — see new-patient and
// edit-patient containers.
export type PatientSaveEvent =
  | { kind: 'create'; payload: PatientCreatePayload }
  | { kind: 'update'; payload: PatientUpdatePayload };

// Dayjs values (not native Date) — the app provides DayJsDateAdapter at root,
// so MatDatepicker min/max comparators expect Dayjs.
const oldDate: Dayjs = dayjs().subtract(100, 'year');
const today: Dayjs = dayjs();

// Rebuild a FormArray from a saved draft array. Empty/missing input keeps the
// form's existing default rows; otherwise the array is cleared and one fresh
// group is pushed per saved item via the supplied factory, then patched.
function rebuildArray<T extends FormGroup<any>>(
  formArray: FormArray<T>,
  savedItems: any[] | undefined,
  pushFactory: () => void,
): void {
  if (!Array.isArray(savedItems) || savedItems.length === 0) {
    return;
  }
  formArray.clear();
  savedItems.forEach((item, i) => {
    pushFactory();
    formArray.at(i)?.patchValue(item);
  });
}

@Component({
    exportAs: 'xcsPatientForm',
    selector: 'xcs-patient-form',
    templateUrl: './patient-form.component.html',
    styleUrls: ['./patient-form.component.scss'],
    imports: [
      CommonModule,
      ReactiveFormsModule,
      MatButtonModule,
      MatCardModule,
      MatDatepickerModule,
      MatDividerModule,
      MatFormFieldModule,
      MatInputModule,
      MatSelectModule,
      XmedicalUiModule,
      PatientDuplicatesBannerComponent,
      PatientContactMethodsFieldsetComponent,
      PatientDoctorsFieldsetComponent,
      PatientBillingContactFieldsetComponent,
      PatientMedicalAidFieldsetComponent,
    ],
    providers: [DatePipe],
})
export class PatientFormComponent implements OnInit, OnDestroy {

  private destroyRef = inject(DestroyRef);
  private datePipe = inject(DatePipe);
  private patientFormService = inject(PatientFormService);
  private draftPersistence = inject<PatientDraftStore>(XCS_PATIENT_DRAFT_STORE);

  // Lookup data + practice settings now flow as inputs from the container
  // (NewPatient / EditPatient), which read them from CoreService. The form
  // itself has no CoreService dependency.
  readonly languages = input<Option[]>([]);
  readonly genders = input<Option[]>([]);
  readonly races = input<Option[]>([]);
  readonly titles = input<Option[]>([]);
  readonly useFileNumberAsPatientProfileId = input(false);
  readonly partialValidationEnabled = input(false);

  // Practice id used inside the draft-key context. Container supplies it from
  // CoreService.selectedPracticeId(). Null/undefined falls through to
  // 'unknown' inside LocalStoragePatientDraftStore.
  readonly selectedPracticeId = input<number | string | null>(null);

  // Search callbacks bound by the container to repository methods. The form
  // forwards these to its child fieldsets so neither the form nor the
  // fieldsets inject DoctorRepositoryService / MedicalSchemesRepositoryService.
  readonly doctorSearch = input.required<(query: string) => Observable<Doctor[]>>();
  readonly medicalSchemeSearch = input.required<(query: string) => Observable<MedicalScheme[]>>();

  // Doctor persist callbacks. The dumb DoctorAddCard / DoctorEditCard return
  // form values via dialogRef.close; the doctors fieldset uses these
  // callbacks to talk to DoctorRepositoryService through the container.
  readonly doctorAddPersist = input.required<(value: Omit<Doctor, 'id'>) => Observable<Doctor>>();
  readonly doctorEditPersist = input.required<(id: number, value: Doctor) => Observable<Doctor>>();

  // Whether the current user can write to this patient. Resolved upstream by
  // the container (which injects PermissionProvider and tracks
  // `permissionsLoaded$`) and passed in as a plain boolean. Drives the form's
  // own action buttons (template gate) and the read-only gate effect below.
  readonly canWrite = input(true);

  // Read-only gate: toggle the entire FormGroup disabled when the user lacks
  // write permission. Emits no value/status changes so the duplicate checker
  // and draft autosave do not fire as a side effect of the toggle.
  // The guard handles the lifecycle gap before `ngOnInit` builds the form.
  private readonly canWriteGate = effect(() => {
    this.canWrite();
    this.applyCanWriteGate();
  });

  // Save trigger — counter signal input. The container wires this from
  // `patientStore.saveCommand`, which the patient top-nav bumps via
  // `patientStore.requestSave()` when its toolbar Save button is pressed.
  //
  // Why a counter and not a direct call? The top-nav lives in the page
  // chrome and the form lives inside a routed view; it has no template ref
  // to call `form.submit()` on. Some indirection is unavoidable.
  //
  // Why not just inject `PatientFormService` into the top-nav and validate
  // + extract + map + persist there? The service holds the FormGroup, but
  // the rest of the save flow (saveError signal, validate signal, [apiError]
  // mapping for failed-API field highlighting, draft cleanup, post-success
  // tab/navigation hooks) lives on this component. A second save path would
  // duplicate that flow or reach back into this component anyway — through
  // some signal or subject. The counter is that signal, made explicit.
  //
  // Why a counter rather than a boolean / void? Re-pressing Save with the
  // same intent has to fire again; a bumping counter captures "another
  // request" without needing the listener to reset state. This also lets us
  // ignore the value-at-mount cleanly: record it once, only fire on later
  // bumps. Without that guard, mounting the form would auto-trigger save.
  //
  // `untracked` isolates `submit()`'s signal writes (saveError, validate,
  // form mutations) from the effect's reactive dependencies — submit() is
  // an imperative call we explicitly want, not a tracked read.
  readonly saveCommand = input<number>(0);
  private lastSaveCommandSeen: number | null = null;
  private readonly saveCommandEffect = effect(() => {
    const currentSaveCommandCounter = this.saveCommand();
    if (this.lastSaveCommandSeen === null) {
      this.lastSaveCommandSeen = currentSaveCommandCounter;
      return;
    }
    if (currentSaveCommandCounter !== this.lastSaveCommandSeen) {
      this.lastSaveCommandSeen = currentSaveCommandCounter;
      untracked(() => this.submit());
    }
  });

  // Status push from the container (typically wired from
  // `patientStore.statusUpdateCommand`). Only meaningful in edit mode — create
  // mode ignores incoming status pushes (the create payload has no status
  // field). Re-selecting the same status id still triggers because the serial
  // advances on every request.
  readonly statusUpdateCommand = input<{ id: number; serial: number }>({ id: 0, serial: 0 });
  private lastStatusSerialSeen: number | null = null;
  private readonly statusUpdateCommandEffect = effect(() => {
    const cmd = this.statusUpdateCommand();
    if (this.lastStatusSerialSeen === null) {
      this.lastStatusSerialSeen = cmd.serial;
      return;
    }
    if (cmd.serial !== this.lastStatusSerialSeen) {
      this.lastStatusSerialSeen = cmd.serial;
      if (this.isEditForm) {
        untracked(() => this.updateStatus(cmd.id));
      }
    }
  });

  private applyCanWriteGate(): void {
    if (!this.form) return;
    if (this.canWrite()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  // Emits whenever the form is replaced (clearSavedFormData rebuilds it). Form-
  // tied subscriptions pipe `takeUntil(formReset$)` so they detach when the
  // underlying form is gone, and `formEventHandler()` re-subscribes against the
  // new form. Component-lifetime streams use `takeUntilDestroyed(destroyRef)`.
  private formReset$ = new Subject<void>();

  form: PatientForm = null;

  // Local UI state — signals so child OnPush bindings see updates without
  // depending on the parent's CD cycle.
  readonly validate = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly showFullErrorMessage = signal(false);

  // Duplicates list flows in from the container (which runs findSimilar). The
  // form computes the query string and emits via (duplicateQueryChanged); the
  // container subscribes, calls the repository, and feeds results back.
  readonly duplicates = input<QueryPatient[]>([]);
  readonly duplicateQueryChanged = output<string>();

  // Bumped on every form.statusChanges emission. Drives any computed signals
  // that reflect form validity / per-array errors / fullFormError.
  private readonly formStatusTick = signal(0);

  readonly fullFormError = computed<any[]>(() => {
    if (!this.showFullErrorMessage()) {
      return [];
    }
    this.formStatusTick();
    return this.patientFormService.printAllFormErrors();
  });

  readonly formValid = computed(() => {
    this.formStatusTick();
    return !!this.form && this.form.valid;
  });

  readonly hasDuplicates = computed(() => this.duplicates().length > 0);

  readonly hasContactMethodError = computed(() => {
    this.formStatusTick();
    if (!this.form) return false;
    return this.hasAtLeastOneValueRequiredError(this.contactNumbers)
      || this.hasAtLeastOneValueRequiredError(this.emailAddresses)
      || this.hasAtLeastOneValueRequiredError(this.addressDetails);
  });

  readonly hasBillingContactMethodError = computed(() => {
    this.formStatusTick();
    if (!this.form) return false;
    return this.hasAtLeastOneValueRequiredError(this.billingContactContactNumbers)
      || this.hasAtLeastOneValueRequiredError(this.billingContactEmailAddresses)
      || this.hasAtLeastOneValueRequiredError(this.billingContactAddressDetails);
  });

  readonly patient = input<Patient | null>(null);
  readonly patientNameChange = output<string>();

  /**
   * Emitted when the form decides to commit. The container is responsible for
   * the persist call (PatientStore.create / .update) and any post-success
   * side effects (open tab, navigate, ask the form to clear its draft via
   * `clearSavedFormData()`).
   */
  readonly save = output<PatientSaveEvent>();

  /**
   * Emitted when the user picks an existing patient from the duplicates
   * banner. The container handles the tab-open + navigation; the form simply
   * resets its state.
   */
  readonly editDuplicateRequested = output<QueryPatient>();

  /**
   * Emitted when the user clicks the form's Cancel button. The container
   * handles the actual route navigation — the form has no Router dependency.
   */
  readonly cancelled = output<void>();

  /**
   * API error from a failed persist, forwarded by the container. When this
   * input changes to a non-null value the form runs the existing
   * field-mapping + saveError signal logic so highlighted controls and the
   * banner appear without the form needing to call the repository itself.
   */
  readonly apiError = input<unknown | null>(null);
  private readonly apiErrorEffect = effect(() => {
    const err = this.apiError();
    if (err == null) return;
    untracked(() => this.handleSaveError(err));
  });

  private debounceTimeOut = 1500;
  private debounceTimeOutShort = 100;

  // Drives the duplicates banner's "tabs are open" visual treatment. Resolved
  // by the container from TabsStore.
  readonly hasTabs = input(false);

  // Optional content for the right column of the identity row in edit mode.
  // Container passes a <ng-template> (typically containing
  // <app-allergies-widget/>); the form lazily instantiates it via
  // *ngTemplateOutlet only when isEditForm is true. Using TemplateRef here
  // (not <ng-content>) avoids the parent-view-instantiation pitfall: with
  // <ng-content>, the projected widget would be created in the container's
  // view regardless of whether the form's @if rendered the slot.
  readonly editRail = input<TemplateRef<unknown> | null>(null);

  get isEditForm(): boolean {
    return !!this.patient();
  }

  get minDobValue(): Dayjs {
    return oldDate;
  }

  get maxDobValue(): Dayjs {
    return today;
  }

  get addressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.form.controls.addressDetails;
  }

  get emailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.form.controls.emailAddresses;
  }

  get contactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.form.controls.contactNumbers;
  }

  get secondaryContacts(): FormArray<FormGroup<SecondaryContactFormShape>> {
    return this.form.controls.secondaryContacts;
  }

  get familyDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.form.controls.familyDoctors;
  }

  get referringDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.form.controls.referringDoctors;
  }

  get billingContact(): FormGroup<BillingContactFormShape> {
    return this.form.controls.billingContact;
  }

  get billingContactAddressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.billingContact.controls.billingContactAddressDetails;
  }

  get billingContactContactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.billingContact.controls.billingContactContactNumbers;
  }

  get billingContactEmailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.billingContact.controls.billingContactEmailAddresses;
  }

  get medicalAid(): FormGroup<MedicalAidFormShape> {
    return this.form.controls.medicalAid;
  }

  private hasAtLeastOneValueRequiredError(formArray: FormArray<FormGroup<any>>): boolean {
    return formArray.controls.some(control => control.get('number')?.hasError('atLeastOnValueRequired') ||
                                              control.get('emailAddress')?.hasError('atLeastOnValueRequired') ||
                                              control.get('address')?.hasError('atLeastOnValueRequired'));
  }

  ngOnInit(): void {
    this.form = this.patientFormService.new(this.patient() ?? undefined, {
      useFileNumberAsPatientProfileId: this.useFileNumberAsPatientProfileId(),
      partialPatientFormValidationEnabled: this.partialValidationEnabled(),
    });
    this.loadFormFromLocalStorage();
    this.formEventHandler();
  }

  ngOnDestroy(): void {
    this.formReset$.next();
    this.formReset$.complete();
    this.patientFormService.reset();
  }

  private genericSaveErrorMessage(): string {
    return this.isEditForm
      ? 'Failed to update patient. Please try again or contact support if the problem persists.'
      : 'Failed to create patient. Please try again or contact support if the problem persists.';
  }

  toggleFullErrorMessage() {
    this.showFullErrorMessage.update(v => !v);
  }

  // Public entry point for "user / container wants to save". Called by:
  //  - the form's own Save / Create button (template (confirm)="submit()")
  //  - the saveCommand effect when the container bumps the counter
  //  - tests, via the form spec
  // Validates and either emits (save) with the payload or surfaces validation
  // errors on the form. Renamed from `save()` so the (save) output emitter
  // does not shadow the method.
  submit() {
    this.saveError.set(null);

    if (this.formValid()) {
      this.savePatient();
    }
    this.validate.set(true);
    this.patientFormService.validateAllFormFields();
  }

  dismissError() {
    this.saveError.set(null);
  }

  setBillingDetailsToPatientDetails() {
    this.billingContact.controls.billingContactFirstName.patchValue(this.form.controls.firstName.value);
    this.billingContact.controls.billingContactSurname.patchValue(this.form.controls.surname.value);
    this.billingContact.controls.billingContactIdentityNumber.patchValue(this.form.controls.identityNumber.value);
    this.billingContact.controls.billingContactIdentityNumberConfirmation.patchValue(this.form.controls.identityNumberConfirmation.value);

    // Clear and rebuild FormArrays - patchValue/setValue doesn't work properly on FormArrays
    this.billingContactContactNumbers.clear();
    this.contactNumbers.controls.forEach(control => {
      this.patientFormService.addBillingContactContactNumber();
      this.billingContactContactNumbers.at(this.billingContactContactNumbers.length - 1).patchValue(control.value);
    });

    this.billingContactAddressDetails.clear();
    this.addressDetails.controls.forEach(control => {
      this.patientFormService.addBillingContactAddress();
      this.billingContactAddressDetails.at(this.billingContactAddressDetails.length - 1).patchValue(control.value);
    });

    this.billingContactEmailAddresses.clear();
    this.emailAddresses.controls.forEach(control => {
      this.patientFormService.addBillingContactEmailAddress();
      this.billingContactEmailAddresses.at(this.billingContactEmailAddresses.length - 1).patchValue(control.value);
    });
  }

  updateStatus(id: number) {
    // Patch the form control only. The top-nav mutates its own
    // `patient.status` synchronously when the user picks an option, so the
    // bound display already reflects the choice — re-mutating it here would
    // cause ExpressionChangedAfterItHasBeenCheckedError on the same input.
    this.form.controls.status.setValue(id);
  }

  // Called from the duplicates banner when the user picks an existing patient
  // to edit instead of creating a new one. The form resets its in-progress
  // state and emits — the container handles tab-opening / navigation.
  editPatient(patient: QueryPatient) {
    this.form.reset();
    this.editDuplicateRequested.emit(patient);
  }

  // Emitted when the user clicks the form's Cancel button. The container
  // navigates back to the appropriate route (dashboard for new, patient
  // detail page for edit) — the form has no Router dependency.
  cancel(): void {
    this.cancelled.emit();
  }

  private get draftContext() {
    // userId is resolved by the draft-store implementation itself
    // (LocalStoragePatientDraftStore reads AuthenticationService); the form
    // only supplies the practice context.
    return {
      practiceId: this.selectedPracticeId() ?? 'unknown',
    };
  }

  private saveFormToLocalStorage(): void {
    if (this.isEditForm) {
      return;
    }
    this.draftPersistence.save(this.form.getRawValue(), this.draftContext);
  }

  private loadFormFromLocalStorage(): void {
    if (this.isEditForm) {
      return;
    }
    const draft = this.draftPersistence.load(this.draftContext);
    if (!draft) {
      return;
    }
    this.applyDraft(draft);
  }

  private applyDraft(draft: PatientDraft): void {
    // Patch scalars first; FormArrays cannot be patched wholesale, so they are
    // rebuilt below. Scalar groups (medicalAid, billingContact scalars) ride
    // along with the top-level patchValue.
    const scalars: Record<string, any> = { ...draft };
    delete scalars.contactNumbers;
    delete scalars.emailAddresses;
    delete scalars.addressDetails;
    delete scalars.secondaryContacts;
    delete scalars.familyDoctors;
    delete scalars.referringDoctors;
    if (scalars.billingContact) {
      const billing = { ...scalars.billingContact };
      delete billing.billingContactContactNumbers;
      delete billing.billingContactEmailAddresses;
      delete billing.billingContactAddressDetails;
      scalars.billingContact = billing;
    }
    this.form.patchValue(scalars);

    rebuildArray(this.contactNumbers, draft.contactNumbers, () => this.patientFormService.addContactNumber());
    rebuildArray(this.emailAddresses, draft.emailAddresses, () => this.patientFormService.addEmailAddress());
    rebuildArray(this.addressDetails, draft.addressDetails, () => this.patientFormService.addAddress());

    rebuildArray(this.familyDoctors, draft.familyDoctors as any[] | undefined,
      () => this.familyDoctors.push(makeDoctorFormGroup()));
    rebuildArray(this.referringDoctors, draft.referringDoctors as any[] | undefined,
      () => this.referringDoctors.push(makeDoctorFormGroup()));

    // Secondary contacts contain a nested contactNumbers FormArray. The default
    // group has one inner row, but a saved secondary may have several. Rebuild
    // the inner array to match the saved length before patching, otherwise
    // patchValue on a FormArray silently drops entries past the existing length.
    const savedSecondaries = (draft.secondaryContacts ?? []) as any[];
    if (savedSecondaries.length > 0) {
      this.secondaryContacts.clear();
      savedSecondaries.forEach(s => {
        const group = makeSecondaryContactFormGroup();
        const savedNumbers = (s?.contactNumbers ?? []) as any[];
        if (savedNumbers.length > 0) {
          group.controls.contactNumbers.clear();
          savedNumbers.forEach(() => group.controls.contactNumbers.push(makeContactNumberFormGroup()));
        }
        this.secondaryContacts.push(group);
        this.secondaryContacts.at(this.secondaryContacts.length - 1)?.patchValue(s);
      });
    }

    const billing = draft.billingContact;
    if (billing) {
      rebuildArray(this.billingContactContactNumbers, billing.billingContactContactNumbers, () => this.patientFormService.addBillingContactContactNumber());
      rebuildArray(this.billingContactEmailAddresses, billing.billingContactEmailAddresses, () => this.patientFormService.addBillingContactEmailAddress());
      rebuildArray(this.billingContactAddressDetails, billing.billingContactAddressDetails, () => this.patientFormService.addBillingContactAddress());
    }
  }

  clearFormFromLocalStorage(): void {
    this.draftPersistence.clear(this.draftContext);
  }

  clearSavedFormData(): void {
    this.clearFormFromLocalStorage();

    // Detach all form-tied subscriptions from the old form, rebuild the form,
    // then re-subscribe against the fresh instance. The new form starts
    // enabled by default; re-apply the read-only gate so canWrite=false
    // users don't get an editable form back.
    this.formReset$.next();
    this.form = this.patientFormService.new(null, {
      useFileNumberAsPatientProfileId: this.useFileNumberAsPatientProfileId(),
      partialPatientFormValidationEnabled: this.partialValidationEnabled(),
    });
    this.formEventHandler();
    this.applyCanWriteGate();

    this.saveError.set(null);
    this.validate.set(false);
    this.showFullErrorMessage.set(false);
    // Container owns the duplicates list now; emit an empty query so it can
    // reset its state without waiting for the next valueChanges tick.
    this.duplicateQueryChanged.emit('');
    // Bump the status-tick signal so computed signals (formValid, hasContact-
    // MethodError, hasBillingContactMethodError, fullFormError) recompute
    // against the new form immediately. Without this, they keep returning
    // their last cached value until the new form emits its first
    // statusChanges event.
    this.formStatusTick.update(v => v + 1);
  }

  private savePatient() {
    const value = this.form.getRawValue();
    if (this.isEditForm) {
      this.save.emit({ kind: 'update', payload: formValueToUpdatePayload(value, this.patient()!) });
    } else {
      this.save.emit({ kind: 'create', payload: formValueToCreatePayload(value) });
    }
  }

  /**
   * Map API errors from a failed PatientStore.create() / .update() onto the
   * form. Replaces the legacy `patientEventService.error$` subscription that
   * read from a fan-out event bus. The argument is whatever the observable
   * errored with — typically an HttpErrorResponse where `.error` is the
   * structured API payload.
   */
  private handleSaveError(err: any): void {
    const apiErrorBody = err?.error;
    if (!apiErrorBody) {
      this.saveError.set(this.genericSaveErrorMessage());
      return;
    }
    const result = CustomValidators.applyApiErrorsToForm(this.form, apiErrorBody);
    if (result.applied.length > 0) {
      this.saveError.set('Please correct the highlighted fields and try again.');
      this.validate.set(true);
    } else if (result.unmappedErrors.length > 0) {
      const errorMessages = result.unmappedErrors.flatMap(e => e.messages);
      this.saveError.set(errorMessages.join('. '));
    } else {
      this.saveError.set(this.genericSaveErrorMessage());
    }
  }

  private formEventHandler() {
    // Form-tied streams complete on `formReset$` (when clearSavedFormData
    // rebuilds the form) and on component destroy.
    const untilFormReset = <T>() => takeUntil<T>(merge(this.formReset$));

    this.form.controls.firstName.valueChanges.pipe(
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formValueChangeHandler());

    this.form.controls.surname.valueChanges.pipe(
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formValueChangeHandler());

    this.form.controls.identityNumber.valueChanges.pipe(
      debounceTime(this.debounceTimeOutShort),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.idControlStatusChangedHandler());

    // Duplicate-patient check: form computes a query string from the tracked
    // fields and emits it. The container subscribes, runs findSimilar (with
    // its own switchMap to cancel in-flight requests), and feeds results back
    // via the [duplicates] input.
    const duplicateTrackedFields: Array<keyof PatientForm['controls']> =
      ['firstName', 'surname', 'identityNumber', 'passportNumber', 'medicalAid', 'contactNumbers', 'emailAddresses', 'dateOfBirth'];
    this.form.valueChanges.pipe(
      filter(() => !this.isEditForm && duplicateTrackedFields.some(f => this.form.controls[f].dirty)),
      debounceTime(this.debounceTimeOut),
      distinctUntilChanged(),
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.duplicateQueryChanged.emit(this.buildDuplicateQuery()));

    if (!this.isEditForm) {
      this.form.valueChanges.pipe(
        debounceTime(this.debounceTimeOut),
        distinctUntilChanged(),
        untilFormReset(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => this.saveFormToLocalStorage());
    }

    this.form.statusChanges.pipe(
      untilFormReset(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.formStatusTick.update(v => v + 1));
  }

  private idControlStatusChangedHandler() {
    const idControl = this.form.controls.identityNumber;
    if (idControl.valid && idControl.value && idControl.value.length > 5) {
      const identityValueString = idControl.value;
      let dobString = identityValueString.substr(0, 2) + '-' + identityValueString.substr(2, 2) + '-' + identityValueString.substr(4, 2);
      const year = dobString.split('-')[0];
      const currentYear = new Date().getFullYear().toString().slice(-2);
      const genderCode = identityValueString.substr(6, 1);
      const gender = parseInt(genderCode, 10) < 5 ? 'F' : 'M';
      const dobControl = this.form.controls.dateOfBirth;
      const genderControl = this.form.controls.gender;
      dobString = year < currentYear ? '20' + dobString : 19 + dobString;
      dobControl.setValue(dobString);
      dobControl.updateValueAndValidity();
      genderControl.setValue(gender);
      genderControl.updateValueAndValidity();
    }
  }

  private formValueChangeHandler() {
    const firstName = this.form.controls.firstName.value || '';
    const surname = this.form.controls.surname.value || '';
    const name = firstName + ' ' + surname;
    if (name === ' ') {
      return;
    }
    this.patientNameChange.emit(name);
  }

  private buildDuplicateQuery(): string {
    const c = this.form.controls;
    const queryParams: string[] = [];
    if (c.firstName.value) { queryParams.push('firstName=' + c.firstName.value); }
    if (c.surname.value) { queryParams.push('surname=' + c.surname.value); }
    if (c.dateOfBirth.value) {
      const dobDate = dayjs.isDayjs(c.dateOfBirth.value) ? c.dateOfBirth.value.toDate() : c.dateOfBirth.value;
      const dobString = this.datePipe.transform(dobDate, 'yyyy-MM-dd 00:00:00');
      queryParams.push('dateOfBirth=' + dobString);
    }
    if (c.identityNumber.value) { queryParams.push('identityNumber=' + c.identityNumber.value); }
    if (c.passportNumber.value) { queryParams.push('passportNumber=' + c.passportNumber.value); }
    if (this.medicalAid.controls.medicalAidNumber.value) {
      queryParams.push('medicalAid.medicalAidNumber=' + this.medicalAid.controls.medicalAidNumber.value || '');
    }
    if (this.medicalAid.controls.dependantCode.value) {
      queryParams.push('medicalAid.dependantCode=' + this.medicalAid.controls.dependantCode.value || '');
    }
    this.contactNumbers.controls.forEach(group => {
      const v = group.controls.number.value;
      if (v) {
        queryParams.push('contactNumbers.' + group.controls.label.value + '=' + v.replace(/\s/g, ''));
      }
    });
    this.emailAddresses.controls.forEach(group => {
      const v = group.controls.emailAddress.value;
      if (v) {
        queryParams.push('emailAddresses.' + group.controls.label.value + '=' + v);
      }
    });
    return queryParams.filter(p => p.length > 0).join(',');
  }
}
